import { useTheme } from "next-themes"
import { Toaster as Sonner, toast } from "sonner"

/**
 * Bookvia-branded toast. Replaces sonner's default "richColors" palette
 * (loud green/red pills) with a soft, cream-and-coral card that matches
 * the rest of the app. Each variant has its own accent stripe on the left
 * so users can tell state (success / error / warning / info) at a glance
 * without the whole toast turning neon.
 */
const Toaster = ({ ...props }) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      duration={5000}
      offset={12}
      // Explicit gap keeps stacked toasts breathable on mobile
      gap={10}
      toastOptions={{
        // Base card style. Rounded 2xl, soft warm shadow, cream background,
        // brand accent color for text, left border used as variant stripe.
        unstyled: false,
        classNames: {
          toast:
            "group toast pointer-events-auto flex w-full items-start gap-3 " +
            "rounded-2xl border border-[#EAE3D9] bg-white text-[#2A2A2A] " +
            "shadow-[0_12px_36px_-8px_rgba(42,42,42,0.18)] " +
            "px-4 py-3.5 text-[14.5px] font-medium leading-snug " +
            "dark:bg-[#1E1A17] dark:text-[#F5F2ED] dark:border-[#332D29]",
          title:
            "font-display font-semibold text-[15px] leading-tight tracking-[-0.005em]",
          description:
            "text-[13px] leading-snug text-[#737373] dark:text-[#A39F99] mt-0.5",
          icon:
            "shrink-0 h-9 w-9 rounded-xl grid place-items-center " +
            "[&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:stroke-[2.4]",
          actionButton:
            "!bg-[#E85A4F] !text-white !rounded-xl !px-3 !py-1.5 !text-[13px] !font-semibold hover:!bg-[#D94A3F]",
          cancelButton:
            "!bg-[#F3EFE9] !text-[#2A2A2A] !rounded-xl !px-3 !py-1.5 !text-[13px] !font-semibold dark:!bg-[#2A2522] dark:!text-[#F5F2ED]",
          closeButton:
            "!bg-[#F3EFE9] !border-0 !text-[#737373] hover:!bg-[#EAE3D9] dark:!bg-[#2A2522] dark:!text-[#A39F99]",
          // ── Variants ─────────────────────────────────────────────
          // Left border acts as a colored stripe; icon box tints match.
          success:
            "!border-l-4 !border-l-[#E85A4F] " +
            "[&_[data-icon]]:!bg-[#E85A4F]/12 [&_[data-icon]>svg]:!text-[#E85A4F]",
          error:
            "!border-l-4 !border-l-[#DC2626] " +
            "[&_[data-icon]]:!bg-[#DC2626]/12 [&_[data-icon]>svg]:!text-[#DC2626]",
          warning:
            "!border-l-4 !border-l-[#D97706] " +
            "[&_[data-icon]]:!bg-[#D97706]/12 [&_[data-icon]>svg]:!text-[#D97706]",
          info:
            "!border-l-4 !border-l-[#2A2A2A] " +
            "[&_[data-icon]]:!bg-[#2A2A2A]/8 [&_[data-icon]>svg]:!text-[#2A2A2A] " +
            "dark:!border-l-[#F5F2ED] dark:[&_[data-icon]>svg]:!text-[#F5F2ED]",
        },
      }}
      {...props} />
  );
}

export { Toaster, toast }
